import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { App, Button, Dropdown, Form, Input, Modal, Select, Table, Tag, Typography } from 'antd';
import type { MenuProps, TableColumnsType } from 'antd';
import {
  DeleteOutlined,
  EditOutlined,
  EnvironmentOutlined,
  IdcardOutlined,
  MoreOutlined,
  PlusOutlined,
  SearchOutlined,
  SortAscendingOutlined,
  SortDescendingOutlined,
  WalletOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { authService } from '../services/auth';
import SupplierContactCell from '../components/suppliers/SupplierContactCell';
import SupplierContactsFormList from '../components/suppliers/SupplierContactsFormList';
import { applySupplierSaveError } from '../components/suppliers/supplierFormErrors';
import {
  canDeleteSupplier,
  canManageSuppliers,
} from '../modules/suppliers/supplierAccessPolicy';
import {
  SupplierContact,
  SupplierContactFormValue,
  normalizeSupplierContacts,
  toSupplierContactFormList
} from '../utils/supplierContacts';
import { matchesSupplierSearch } from '../modules/reference-data/supplierSearch';
import './Suppliers.css';

const { Text } = Typography;

const CURRENCIES = ['USD', 'EUR', 'PLN', 'GBP', 'CNY'];

interface Supplier {
  id: string;
  name: string;
  contactPerson?: string | null;
  contactPeople?: SupplierContact[] | null;
  address?: string | null;
  taxId?: string | null;
  email?: string | null;
  phone?: string | null;
  defaultCurrency?: string | null;
}

interface SupplierFormValues {
  name: string;
  contactPeople?: SupplierContactFormValue[];
  taxId?: string;
  address?: string;
  defaultCurrency?: string;
}

type SupplierSortOrder = 'nameAsc' | 'nameDesc';

const EMPTY_VALUE = '—';

const Suppliers: React.FC = () => {
  const { t } = useTranslation(['suppliers', 'common']);
  const { message, modal } = App.useApp();
  const [searchParams] = useSearchParams();
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState<Supplier | null>(null);
  const [nipFetching, setNipFetching] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();

  // Search and sort states
  const supplierSearchParam = searchParams.get('search') || '';
  const [searchText, setSearchText] = useState(supplierSearchParam);
  const [sortOrder, setSortOrder] = useState<SupplierSortOrder>('nameAsc');

  const currentUser = authService.getCurrentUser();
  const canEditSuppliers = canManageSuppliers(currentUser);
  const canDeleteSuppliers = canDeleteSupplier(currentUser);
  const editingPrimaryContact = editingSupplier
    ? normalizeSupplierContacts(
        editingSupplier.contactPeople,
        editingSupplier.contactPerson,
        editingSupplier.phone,
        editingSupplier.email
      )[0] || null
    : null;
  const allowBlankPrimaryName = Boolean(
    editingPrimaryContact &&
    !editingPrimaryContact.name &&
    (editingPrimaryContact.phone || editingPrimaryContact.email)
  );

  useEffect(() => {
    fetchSuppliers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setSearchText(supplierSearchParam);
  }, [supplierSearchParam]);

  const fetchSuppliers = async () => {
    setLoading(true);
    try {
      const response = await api.get('/suppliers');
      setSuppliers(response.data);
    } catch (error) {
      message.error(t('suppliers:messages.fetchFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleNipLookup = async () => {
    const taxId = form.getFieldValue('taxId');
    if (!taxId) {
      message.warning(t('suppliers:messages.nipEnterFirst'));
      return;
    }

    setNipFetching(true);
    try {
      const response = await api.post('/suppliers/lookup-nip', { nip: taxId });
      const data = response.data;

      form.setFieldsValue({
        name: data.name,
        address: data.address || '',
      });

      if (data.nip) {
        form.setFieldValue('taxId', data.nip);
      }

      message.success(t('suppliers:messages.nipLookupSuccess'));
    } catch (error: any) {
      const code = error.response?.data?.code;
      if (code === 'NIP_INVALID') {
        message.error(t('suppliers:messages.nipInvalid'));
      } else if (code === 'NIP_NOT_FOUND') {
        message.warning(t('suppliers:messages.nipNotFound'));
      } else {
        message.error(t('suppliers:messages.nipLookupFailed'));
      }
    } finally {
      setNipFetching(false);
    }
  };

  const resetSupplierFormState = () => {
    form.resetFields();
    form.setFieldsValue({ contactPeople: toSupplierContactFormList() });
  };

  const handleSubmit = async (values: SupplierFormValues) => {
    setSubmitting(true);
    form.setFields([
      { name: 'name', errors: [] },
      { name: 'contactPeople', errors: [] },
      { name: 'taxId', errors: [] }
    ]);

    try {
      const contactPeople = normalizeSupplierContacts(values.contactPeople);
      const primaryContact = contactPeople[0] || null;

      // Clean form data: convert empty strings to null for optional fields
      const cleanedValues = {
        name: values.name,
        contactPeople,
        contactPerson: primaryContact?.name || null,
        email: primaryContact?.email || null,
        phone: primaryContact?.phone || null,
        taxId: values.taxId || null,
        address: values.address || null,
        defaultCurrency: values.defaultCurrency || 'PLN',
      };

      if (editingSupplier) {
        await api.put(`/suppliers/${editingSupplier.id}`, cleanedValues);
        message.success(t('suppliers:messages.updateSuccess'));
      } else {
        await api.post('/suppliers', cleanedValues);
        message.success(t('suppliers:messages.createSuccess'));
      }

      setModalVisible(false);
      setEditingSupplier(null);
      resetSupplierFormState();
      fetchSuppliers();
    } catch (error) {
      applySupplierSaveError(error, form, t, message, t('suppliers:messages.saveFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (supplier: Supplier) => {
    setEditingSupplier(supplier);
    form.setFieldsValue({
      name: supplier.name,
      contactPeople: toSupplierContactFormList(
        supplier.contactPeople,
        supplier.contactPerson,
        supplier.phone,
        supplier.email
      ),
      taxId: supplier.taxId || '',
      address: supplier.address || '',
      defaultCurrency: supplier.defaultCurrency || undefined,
    });
    setModalVisible(true);
  };

  const handleDelete = async (supplierId: string) => {
    try {
      await api.delete(`/suppliers/${supplierId}`);
      message.success(t('suppliers:supplierDeleted'));
      fetchSuppliers();
    } catch (error: any) {
      const code = error.response?.data?.code;
      if (code === 'SUPPLIER_IN_USE') {
        message.error(t('suppliers:messages.deleteInUse'));
      } else {
        message.error(t('suppliers:messages.deleteError'));
      }
    }
  };

  const handleModalClose = () => {
    setModalVisible(false);
    setEditingSupplier(null);
    resetSupplierFormState();
  };

  // Filter and sort suppliers
  const filteredAndSortedSuppliers = useMemo(() => {
    let filtered = [...suppliers];

    // Apply search filter
    if (searchText) {
      filtered = filtered.filter((supplier) => matchesSupplierSearch(supplier, searchText));
    }

    // Apply sort
    filtered.sort((a, b) => {
      if (sortOrder === 'nameAsc') {
        return (a.name || '').localeCompare(b.name || '');
      } else {
        return (b.name || '').localeCompare(a.name || '');
      }
    });

    return filtered;
  }, [suppliers, searchText, sortOrder]);

  const renderSupplierIdentity = (supplier: Supplier) => (
    <div className="supplier-identity">
      <div className="supplier-identity__content">
        <Text className="supplier-identity__name" title={supplier.name}>
          {supplier.name || EMPTY_VALUE}
        </Text>
        <div className="supplier-identity__chips">
          <Tag className="supplier-chip supplier-chip--tax">
            <IdcardOutlined />
            {supplier.taxId || t('suppliers:meta.noTaxId')}
          </Tag>
          <Tag className="supplier-chip supplier-chip--currency">
            <WalletOutlined />
            {supplier.defaultCurrency || 'PLN'}
          </Tag>
        </div>
      </div>
    </div>
  );

  const renderAddress = (address: string | null | undefined) => (
    <span className="supplier-address">
      <EnvironmentOutlined />
      <span title={address || undefined}>{address || EMPTY_VALUE}</span>
    </span>
  );

  const columns: TableColumnsType<Supplier> = [
    {
      title: t('suppliers:table.supplierInfo'),
      key: 'name',
      width: 360,
      render: (_value: unknown, record: Supplier) => renderSupplierIdentity(record),
    },
    {
      title: t('suppliers:table.contactPeople'),
      key: 'contactPeople',
      width: 280,
      render: (_value: unknown, record: Supplier) => (
        <SupplierContactCell
          contacts={record.contactPeople}
          contactPerson={record.contactPerson}
          phone={record.phone}
          email={record.email}
          emptyValue={EMPTY_VALUE}
        />
      ),
    },
    {
      title: t('suppliers:table.address'),
      dataIndex: 'address',
      key: 'address',
      width: 360,
      render: renderAddress,
    },
  ];

  const getRowMenuItems = (supplier: Supplier): MenuProps['items'] => {
    const items: NonNullable<MenuProps['items']> = [];

    if (canEditSuppliers) {
      items.push({
        key: 'edit',
        icon: <EditOutlined />,
        label: t('suppliers:menu.edit')
      });
    }

    if (canDeleteSuppliers) {
      items.push({
        key: 'delete',
        icon: <DeleteOutlined />,
        label: t('suppliers:menu.delete'),
        danger: true
      });
    }

    return items;
  };

  const confirmDelete = async (supplierId: string) => {
    const confirmed = await new Promise<boolean>((resolve) => {
      modal.confirm({
        title: t('suppliers:confirmDeleteSupplier'),
        okText: t('common:yes'),
        cancelText: t('common:no'),
        okButtonProps: { danger: true },
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });

    if (!confirmed) {
      return;
    }

    await handleDelete(supplierId);
  };

  if (canEditSuppliers || canDeleteSuppliers) {
    columns.push({
      title: '',
      key: 'menu',
      width: 56,
      align: 'right',
      render: (_value: unknown, record: Supplier) => (
        <Dropdown
          trigger={['click']}
          menu={{
            items: getRowMenuItems(record),
            onClick: ({ key, domEvent }) => {
              domEvent.stopPropagation();

              if (key === 'edit') {
                handleEdit(record);
              }

              if (key === 'delete') {
                void confirmDelete(record.id);
              }
            }
          }}
        >
          <Button
            type="text"
            icon={<MoreOutlined />}
            onClick={(event) => event.stopPropagation()}
            aria-label={t('suppliers:menu.open')}
          />
        </Dropdown>
      ),
    });
  }

  return (
    <div className="suppliers-page">
      <div className="suppliers-page__header">
        <h1>{t('suppliers:title')}</h1>
        {canEditSuppliers && (
          <Button
            className="suppliers-page__create-button"
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditingSupplier(null);
              resetSupplierFormState();
              setModalVisible(true);
            }}
          >
            {t('suppliers:newSupplier')}
          </Button>
        )}
      </div>

      <div className="suppliers-page__toolbar">
        <Input
          className="suppliers-page__quick-search"
          placeholder={t('suppliers:supplierSearchPlaceholder')}
          prefix={<SearchOutlined />}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          allowClear
        />
        <Select
          className="suppliers-page__sort"
          value={sortOrder}
          onChange={setSortOrder}
          suffixIcon={sortOrder === 'nameAsc' ? <SortAscendingOutlined /> : <SortDescendingOutlined />}
        >
          <Select.Option value="nameAsc">{t('suppliers:sortNameAsc')}</Select.Option>
          <Select.Option value="nameDesc">{t('suppliers:sortNameDesc')}</Select.Option>
        </Select>
        <span className="suppliers-page__count">
          {filteredAndSortedSuppliers.length}
        </span>
      </div>

      <Table
        className="suppliers-table"
        columns={columns}
        dataSource={filteredAndSortedSuppliers}
        rowKey="id"
        loading={loading}
        rowClassName="suppliers-table__row"
        scroll={{ x: 1060 }}
        locale={{
          emptyText: searchText ? t('suppliers:noSuppliersFound') : t('suppliers:noSuppliers'),
        }}
      />

      <Modal
        title={editingSupplier ? t('suppliers:editSupplier') : t('suppliers:createSupplier')}
        open={modalVisible}
        onCancel={handleModalClose}
        maskClosable={false}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          onFinishFailed={() => message.warning(t('suppliers:messages.fillRequired'))}
          scrollToFirstError
        >
          <Form.Item
            name="name"
            label={t('suppliers:form.name')}
            rules={[{ required: true, message: t('suppliers:validation.nameRequired') }]}
          >
            <Input />
          </Form.Item>
          <SupplierContactsFormList
            addLabel={t('suppliers:form.addContact')}
            allowBlankPrimaryName={allowBlankPrimaryName}
            contactLabel={t('suppliers:form.contact')}
            emailLabel={t('suppliers:form.contactEmail')}
            emailRequiredMessage={t('suppliers:validation.contactEmailRequired')}
            nameLabel={t('suppliers:form.contactName')}
            nameRequiredMessage={t('suppliers:validation.contactNameRequired')}
            phoneLabel={t('suppliers:form.contactPhone')}
            phoneRequiredMessage={t('suppliers:validation.contactPhoneRequired')}
            removeLabel={t('suppliers:form.removeContact')}
          />
          <Form.Item
            name="taxId"
            label={t('suppliers:form.taxId')}
          >
            <Input.Search
              placeholder={t('suppliers:form.enterTaxId')}
              enterButton={t('suppliers:form.lookupNip')}
              loading={nipFetching}
              onSearch={handleNipLookup}
            />
          </Form.Item>
          <Form.Item
            name="defaultCurrency"
            label={t('suppliers:form.defaultCurrency')}
          >
            <Select
              allowClear
              placeholder="—"
              getPopupContainer={(trigger) => trigger.parentNode as HTMLElement}
            >
              {CURRENCIES.map(currency => (
                <Select.Option key={currency} value={currency}>
                  {currency}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="address"
            label={t('suppliers:form.address')}
          >
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={submitting}>
              {editingSupplier
                ? `${t('suppliers:form.update')} ${t('suppliers:supplier')}`
                : `${t('suppliers:form.create')} ${t('suppliers:supplier')}`}
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Suppliers;
